/**
 * Auto-fits the closed desktop Collection Sort <select> (blocks/filters.liquid's
 * .paw-desktop-top-controls .paw-toolbar-sorting) to the width of its
 * currently selected option's own text, instead of a fixed width sized for
 * the longest possible option.
 *
 * Scoped entirely to that one select via SELECT_SELECTOR -- does not touch
 * the native select's open popup (browser/OS-controlled, left alone), the
 * mobile drawer's own sort select, any other <select> on the page, sorting
 * logic, filters, or Section Rendering. Sets a CSS custom property the
 * existing width declaration already prefers (see blocks/filters.liquid);
 * if this script never runs, the control falls back to the Theme Editor's
 * configured fixed width, so there is no unstyled/zero-width state.
 */

// Module-level guard: `type="module"` scripts are already deduped by the
// browser per exact src URL, but this is cheap defense-in-depth against any
// edge case -- without it, a second execution would create a second
// observer and a second pair of document-level `change`/resize listeners.
if (!window.__pawSortAutoWidthInitialized) {
  window.__pawSortAutoWidthInitialized = true;

  const TOOLBAR_SELECTOR = '.paw-desktop-top-controls';
  const SELECT_SELECTOR = `${TOOLBAR_SELECTOR} .paw-toolbar-sorting .sorting-filter__select`;
  const DESKTOP_BREAKPOINT = 750; // matches the theme's existing 749px/750px mobile/desktop split

  // Small fixed safety margin for measurement rounding/kerning. Not a
  // per-option map -- applies uniformly to every option.
  const SAFETY_BUFFER_PX = 4;
  const RESIZE_DEBOUNCE_MS = 150;

  // Absolute last-resort fallbacks, used only when the Theme Editor's
  // configured min/max weren't readable at all (missing data attributes,
  // e.g. an older cached section render) -- never exposed as a setting
  // themselves, just a safety net so a width is always valid.
  const FALLBACK_MIN_WIDTH_PX = 70;
  const FALLBACK_MAX_WIDTH_PX = 400;

  /** @type {HTMLCanvasElement | undefined} */
  let measureCanvas;

  /**
   * @param {string} text
   * @param {string} font
   * @returns {number}
   */
  function measureTextWidth(text, font) {
    if (!measureCanvas) measureCanvas = document.createElement('canvas');
    const context = measureCanvas.getContext('2d');
    if (!context) return NaN;
    context.font = font;
    return context.measureText(text).width;
  }

  /**
   * Parses a data-attribute string as a finite, non-negative number, or
   * returns undefined if it's missing/unparseable -- callers decide their
   * own fallback per value, nothing here is hardcoded into the parsing
   * itself.
   * @param {string | null | undefined} value
   * @returns {number | undefined}
   */
  function parsePositiveNumber(value) {
    if (value == null || value === '') return undefined;
    const parsed = parseFloat(value);
    if (!Number.isFinite(parsed) || parsed < 0) return undefined;
    return parsed;
  }

  /**
   * Reads sorting_width_mode/sorting_min_width/sorting_max_width from the
   * data attributes snippets/sorting.liquid writes onto the wrapper (driven
   * by the Theme Editor settings on blocks/filters.liquid -- nothing here
   * is hardcoded). Normalizes malformed combinations safely:
   *  - missing/unparseable min or max fall back to fixed, sane defaults;
   *  - a negative value is treated as missing (see parsePositiveNumber);
   *  - if min ends up greater than max, min is lowered to match max, so the
   *    clamp below can never be an empty/invalid range.
   * @param {HTMLElement} wrapper
   */
  function readWidthConfig(wrapper) {
    const mode = wrapper.dataset.sortingWidthMode === 'fixed' ? 'fixed' : 'auto_selected';

    const min = parsePositiveNumber(wrapper.dataset.sortingMinWidth) ?? FALLBACK_MIN_WIDTH_PX;
    const max = parsePositiveNumber(wrapper.dataset.sortingMaxWidth) ?? FALLBACK_MAX_WIDTH_PX;

    return {
      mode,
      minWidth: Math.min(min, max),
      maxWidth: max,
    };
  }

  /**
   * Formula (auto_selected mode only -- fixed mode skips all of this, see
   * below): measured selected-option text width + select's own left/right
   * padding + wrapper's left/right padding + wrapper's left/right border
   * widths + a small fixed safety buffer, then clamped between the
   * configured min and max width. Each component is read once from live
   * computed styles, so nothing here is double-counted.
   *
   * This formula assumes the select's own content box actually fills the
   * wrapper's content box (select width: 100%, min-width: 0 -- see
   * .paw-toolbar-sorting .sorting-filter__select in blocks/filters.liquid).
   * Without min-width: 0, a flex item's default automatic minimum width
   * lets a <select> render narrower than 100% (its own intrinsic content
   * width wins instead), which desyncs the select's actual rendered width
   * from what this formula computes for the wrapper -- that mismatch, not
   * the text measurement, was the cause of the select briefly showing
   * ellipsized text right after a change.
   *
   * Arrow reserve: the select's own CSS already reserves space for the
   * native dropdown arrow via padding-inline-end (see
   * .paw-toolbar-sorting .sorting-filter__select in blocks/filters.liquid).
   * That padding is included automatically by reading
   * selectStyle.paddingRight below -- no separate arrow constant is added
   * here, since doing so would double-count it.
   * @param {HTMLSelectElement} select
   */
  function applyAutoWidth(select) {
    if (!(select instanceof HTMLSelectElement)) return;

    // Skip work entirely for a select that isn't actually visible right now
    // (offsetParent is null when an ancestor is display:none, e.g. the
    // toolbar hidden at <=749px) -- avoids wasted layout reads, and avoids
    // computed styles being unreliable for a display:none subtree.
    if (select.offsetParent === null) return;

    const wrapper = select.closest('.sorting-filter__select-wrapper');
    if (!(wrapper instanceof HTMLElement)) return;

    const { mode, minWidth, maxWidth } = readWidthConfig(wrapper);

    // Fixed mode: skip all text measurement and leave
    // --paw-sorting-computed-width unset/untouched entirely, so the CSS
    // width declaration's fallback to --paw-sorting-width (the Theme
    // Editor's configured fixed width) takes over on its own. Section
    // Rendering re-renders this wrapper with the same data-sorting-width-mode
    // attribute each time, so this mode is preserved across every re-render
    // without this script needing to do anything special.
    if (mode === 'fixed') return;

    const selectedOption = select.options[select.selectedIndex];
    const text = selectedOption ? selectedOption.text : '';

    let selectStyle, wrapperStyle;
    try {
      selectStyle = getComputedStyle(select);
      wrapperStyle = getComputedStyle(wrapper);
    } catch (_error) {
      return;
    }

    const font = `${selectStyle.fontWeight} ${selectStyle.fontSize} ${selectStyle.fontFamily}`;
    const textWidth = measureTextWidth(text, font);
    const selectPadding = parseFloat(selectStyle.paddingLeft) + parseFloat(selectStyle.paddingRight);
    const wrapperPadding = parseFloat(wrapperStyle.paddingLeft) + parseFloat(wrapperStyle.paddingRight);
    const wrapperBorder = parseFloat(wrapperStyle.borderLeftWidth) + parseFloat(wrapperStyle.borderRightWidth);

    const measuredWidth = textWidth + selectPadding + wrapperPadding + wrapperBorder + SAFETY_BUFFER_PX;

    // Guards against NaN from any of the reads above (e.g. computed styles
    // being transiently unreliable mid-morph) -- setting a custom property
    // to an invalid value does NOT reliably trigger its var() fallback, so
    // it's safer to just leave the previous value in place than risk an
    // "auto" collapse.
    if (!Number.isFinite(measuredWidth)) return;

    const finalWidth = Math.ceil(Math.min(Math.max(measuredWidth, minWidth), maxWidth));

    wrapper.style.setProperty('--paw-sorting-computed-width', `${finalWidth}px`);
  }

  function isDesktopViewport() {
    return window.innerWidth >= DESKTOP_BREAKPOINT;
  }

  function measureAll() {
    if (!isDesktopViewport()) return;
    document.querySelectorAll(SELECT_SELECTOR).forEach((select) => applyAutoWidth(select));
  }

  /**
   * True if `node` either IS the toolbar/select, or contains one -- covers
   * both directions of a wholesale replacement:
   *  - the whole toolbar div was swapped in as a fresh node (node itself
   *    matches TOOLBAR_SELECTOR or SELECT_SELECTOR);
   *  - a wider ancestor was swapped in and the toolbar/select is somewhere
   *    inside it (node.querySelector finds it).
   * @param {Node} node
   * @returns {boolean}
   */
  function nodeContainsDesktopSort(node) {
    if (!(node instanceof Element)) return false;

    return (
      node.matches(TOOLBAR_SELECTOR) ||
      node.matches(SELECT_SELECTOR) ||
      node.querySelector(TOOLBAR_SELECTOR) !== null ||
      node.querySelector(SELECT_SELECTOR) !== null
    );
  }

  /**
   * Batches every mutation record in one callback invocation into a single
   * Set of selects to re-measure (never more than once each), and bails out
   * before touching layout/computed-style APIs for any mutation that isn't
   * actually relevant -- this is what keeps a section-wide observer root
   * cheap: the observer must see all mutations to reliably survive Section
   * Rendering replacing/patching the toolbar in ways that can't be
   * predicted in advance, but the expensive work (measurement) only ever
   * runs for the handful of mutations that are actually relevant.
   *
   * A mutation is relevant when EITHER:
   *  - mutation.target itself is inside the toolbar (the common case: an
   *    attribute/child update patched in place, e.g. options changing on
   *    the existing select); or
   *  - a wholesale replacement happened one level up -- Section Rendering
   *    morph can replace the toolbar (or an ancestor of it) entirely, in
   *    which case mutation.target is the STABLE PARENT the new nodes were
   *    inserted into (e.g. results-list), which does NOT itself match
   *    TOOLBAR_SELECTOR, so relying on target.closest(TOOLBAR_SELECTOR)
   *    alone would silently miss it. Checking addedNodes (and removedNodes,
   *    so a fully-removed-then-not-yet-replaced toolbar doesn't leave a
   *    stale computed width behind) catches this case too.
   * @param {MutationRecord[]} mutations
   */
  function handleMutations(mutations) {
    if (!isDesktopViewport()) return;

    /** @type {Set<HTMLSelectElement>} */
    const selectsToMeasure = new Set();

    for (const mutation of mutations) {
      if (mutation.type !== 'childList') continue;

      const target = mutation.target;
      const targetIsRelevant = target instanceof Element && target.closest(TOOLBAR_SELECTOR) !== null;

      const addedIsRelevant = Array.from(mutation.addedNodes).some(nodeContainsDesktopSort);
      const removedIsRelevant = Array.from(mutation.removedNodes).some(nodeContainsDesktopSort);

      if (!targetIsRelevant && !addedIsRelevant && !removedIsRelevant) continue;

      // Re-query fresh from the document root rather than trusting any
      // specific node reference from this mutation -- correct regardless of
      // whether the toolbar was patched in place, replaced wholesale, or
      // moved, and naturally no-ops (finds nothing) for the
      // removed-and-not-yet-replaced case.
      document.querySelectorAll(SELECT_SELECTOR).forEach((select) => {
        if (select instanceof HTMLSelectElement) selectsToMeasure.add(select);
      });
    }

    selectsToMeasure.forEach((select) => applyAutoWidth(select));
  }

  /** @type {number | undefined} */
  let resizeTimeout;

  function handleResize() {
    clearTimeout(resizeTimeout);
    resizeTimeout = window.setTimeout(measureAll, RESIZE_DEBOUNCE_MS);
  }

  function init() {
    measureAll();

    // Native change: fires immediately on selection, before any Section
    // Rendering request even starts, so the control resizes right away.
    // One delegated listener for the whole page -- never one per select.
    document.addEventListener('change', (event) => {
      if (!(event.target instanceof Element)) return;
      const select = event.target.closest(SELECT_SELECTOR);
      if (!(select instanceof HTMLSelectElement)) return;

      // Deferred a frame so this never runs inside the same synchronous
      // tick as Horizon's own delegated change handling (updateFilterAndSorting)
      // -- purely a passivity guarantee, this module only ever reads text/styles
      // and writes a CSS custom property, never sorting state.
      requestAnimationFrame(() => {
        applyAutoWidth(select);
      });
    });

    // facets.js morphs/replaces this toolbar's markup after a sort or
    // filter change resolves via Section Rendering. Which exact node ends
    // up holding the select can't be assumed stable across every morph
    // strategy, so the observer is attached to a section-wide root that's
    // never itself replaced -- but every mutation callback immediately
    // bails out unless it's actually inside the toolbar (see
    // handleMutations), so grid/product-card mutations elsewhere in the
    // section are cheaply ignored rather than triggering measurement.
    // Exactly one observer is created here, for the page's whole lifetime.
    const observerRoot = document.querySelector('results-list, .collection-wrapper') || document.body;
    const observer = new MutationObserver(handleMutations);
    observer.observe(observerRoot, { childList: true, subtree: true });

    // Re-measure once web fonts finish loading -- a cold-loaded custom font
    // can change text metrics after the first synchronous measurement.
    // Guarded by the module-level init flag above, so this can only ever
    // be registered once per page.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(measureAll).catch(() => {});
    }

    // Only needed to catch crossing the mobile/desktop breakpoint by
    // resizing an already-open desktop browser window -- passive and
    // debounced, not a poll (it does nothing unless a resize actually
    // fires).
    window.addEventListener('resize', handleResize, { passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
}
